import { t } from 'i18next';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocalStorage } from 'usehooks-ts';

import { authService } from 'auth/services';
import { IAuthForm, IAuthUser, IUserAutocomplete } from 'auth/types';
import { useBugsnagContext } from 'bugsnag/state';
import { LocalStorageKeys, NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { localStorage, objectKeysCamelToSnakeCase } from 'core/utils';

const STALE_TIME = 1000 * 60 * 60 * 2; //2 hours

export const useAuthUser = (): {
  user: IAuthUser | null;
  isLoading: boolean;
  isFetched: boolean;
} => {
  const [loggedUser, setLoggedUser] = useLocalStorage<IAuthUser | null>(
    LocalStorageKeys.LOGGED_USER,
    null
  );

  const queryClient = useQueryClient();
  const { setBugsnagUser } = useBugsnagContext();

  const { data, isLoading, isFetched } = useQuery(
    ['authUser'],
    ({ signal }) => authService.getAuthUser(signal),
    {
      keepPreviousData: true,
      staleTime: STALE_TIME,
      onSuccess: (data) => {
        setLoggedUser(data.record);
        setBugsnagUser(data.record);

        queryClient.invalidateQueries(['colorsTheme', 'fontsTheme', 'fontFamiliesTheme']);
        queryClient.invalidateQueries(['quote']);
      },
      onSettled: () => {
        const removeLocalStorageLoggedUser = queryClient.getQueriesData(
          'removeLocalStorageLoggedUser'
        );

        const shouldRemove = removeLocalStorageLoggedUser[0]?.[1];

        if (!shouldRemove) {
          return;
        }

        queryClient.setQueryData('removeLocalStorageLoggedUser', false);
        localStorage.remove(LocalStorageKeys.LOGGED_USER);
      },
    }
  );

  return {
    user: loggedUser || data?.record || null,
    isLoading: !loggedUser && isLoading,
    isFetched,
  };
};

export const useAuthHeaders = () => {
  const { user } = useAuthUser();
  const accessToken = user?.accessToken;

  const headers = useMemo(() => {
    if (!accessToken) {
      return {};
    }

    return {
      Authorization: `Bearer ${accessToken}`,
    };
  }, [accessToken]);

  return headers;
};

export const useAuthActions = () => ({
  ...useSignIn(),
  ...useSsoSignIn(),
  ...useImpersonate(),
  ...useRecentImpersonatedUsers(),
});

const useSsoSignIn = () => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageSignInInProgress') },
    error: { title: t('errorSigningIn') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation(authService.ssoSignIn, {
    ...errors,
  });

  const ssoSignIn = (data: Record<string, unknown>) => mutateAsync(data);

  return { ssoSignIn };
};

const useSignIn = () => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageSignInInProgress') },
    error: { title: t('errorSigningIn') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation(authService.signIn, {
    ...errors,
  });

  const signIn = (data: IAuthForm) => mutateAsync(data);

  return { signIn };
};

const useImpersonate = () => {
  const { errors } = useHandleErrors({
    success: (_, data) => {
      const user = data.impersonatedUser;

      return {
        message: t('messageImpersonateUserSuccess', { name: `${user.firstName} ${user.lastName}` }),
      };
    },
    loading: { message: t('messageImpersonateUserInProgress') },
    error: { title: t('errorTitleImpersonateUser') },
    position: NotificationPosition.FORM,
    invalidateQueries: ['authUser'],
  });

  const { mutateAsync } = useMutation(authService.impersonate, {
    ...errors,
  });

  return { impersonate: mutateAsync };
};

const useRecentImpersonatedUsers = () => {
  const [impersonation, setImpersonation] = useLocalStorage<{
    mostRecentUsed: IUserAutocomplete[];
  }>(LocalStorageKeys.IMPERSONATION, { mostRecentUsed: [] });

  const addRecentImpersonatedUser = (user: IUserAutocomplete) => {
    setImpersonation((prevState) => {
      const filteredUsers = prevState.mostRecentUsed.filter(
        (existingUser) => Number(existingUser.id) !== Number(user.id)
      );

      return {
        mostRecentUsed: [objectKeysCamelToSnakeCase(user), ...filteredUsers].slice(0, 5),
      };
    });
  };

  return {
    mostRecentImpersonatedUsers: impersonation.mostRecentUsed || [],
    addRecentImpersonatedUser,
  };
};
